import { useEffect, useState } from 'react';
import { ArrowLeft, Save, ShieldCheck } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card } from '@/Components/ui/Card';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { Textarea } from '@/Components/ui/Textarea';
import { PageHeader } from '@/Components/data/PageHeader';
import { Field } from '@/Components/forms/Field';
import FormStickyFooter from '@/Components/forms/FormStickyFooter';
import qualidadeInspecaoService, { type QualidadeInspecaoInput, type QualidadeResultado, type QualidadeTipo } from '@/services/qualidadeInspecao';
import loteService, { type Lote } from '@/services/lote';
import ordemProducaoService, { type OrdemProducao } from '@/services/ordemProducao';
import { getUuidCandidate } from '@/utils/uuid';

const FORM_VAZIO: QualidadeInspecaoInput = { tipo: 'ENTRADA', lote_id: '', op_id: '', data_inspecao: '', resultado: 'APROVADO', acao_corretiva: '', responsavel: '', observacoes: '', checklist: [] };

export default function QualidadeForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editando = Boolean(id);
  const [form, setForm] = useState<QualidadeInspecaoInput>(FORM_VAZIO);
  const [lotes, setLotes] = useState<Lote[]>([]);
  const [ordens, setOrdens] = useState<OrdemProducao[]>([]);
  const [loading, setLoading] = useState(editando);
  const [salvando, setSalvando] = useState(false);
  const lotesValidos = lotes.filter((lote) => getUuidCandidate(lote));
  const ordensValidas = ordens.filter((op) => getUuidCandidate(op));

  useEffect(() => {
    loteService.listar().then(setLotes).catch(console.error);
    ordemProducaoService.listar().then(setOrdens).catch(console.error);
  }, []);
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    qualidadeInspecaoService.buscarPorId(id).then((data) => setForm({
      tipo: data.tipo,
      lote_id: data.lote_id || '',
      op_id: data.op_id || '',
      data_inspecao: data.data_inspecao || '',
      resultado: data.resultado,
      checklist: data.checklist || [],
      acao_corretiva: data.acao_corretiva || '',
      responsavel: data.responsavel || '',
      observacoes: data.observacoes || '',
    })).catch(console.error).finally(() => setLoading(false));
  }, [id]);

  const salvar = async () => {
    if (!form.tipo || !form.resultado) return;
    setSalvando(true);
    try {
      if (id) await qualidadeInspecaoService.atualizar(id, form);
      else await qualidadeInspecaoService.criar(form);
      navigate('/producao/qualidade');
    } catch (error) {
      console.error('Erro ao salvar inspeção', error);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <form className="nx-page nx-sngpc-form-page" onSubmit={(event) => { event.preventDefault(); void salvar(); }}>
      <PageHeader icon={<ShieldCheck size={21} />} title={editando ? 'Editar Inspeção' : 'Nova Inspeção'} description="Registre o resultado de inspeção de entrada, processo ou final." actions={<Button type="button" variant="ghost" onClick={() => navigate('/producao/qualidade')}><ArrowLeft size={16} /> Voltar</Button>} />
      <Card className="nx-page-card nx-sngpc-form-card">
        <section className="nx-sngpc-form-section">
          <div className="nx-sngpc-form-section__header"><h2>Dados da inspeção</h2></div>
          <div className="nx-sngpc-page-form-grid">
            <Field label="Tipo *" htmlFor="qualidade-tipo"><Select id="qualidade-tipo" value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value as QualidadeTipo })} disabled={loading} required><option value="ENTRADA">Entrada</option><option value="PROCESSO">Processo</option><option value="FINAL">Final</option></Select></Field>
            <Field label="Resultado *" htmlFor="qualidade-resultado"><Select id="qualidade-resultado" value={form.resultado} onChange={(e) => setForm({ ...form, resultado: e.target.value as QualidadeResultado })} disabled={loading} required><option value="APROVADO">Aprovado</option><option value="REPROVADO">Reprovado</option><option value="APROVADO_COM_RESSALVAS">Aprovado com ressalvas</option></Select></Field>
            <Field label="Lote" htmlFor="qualidade-lote"><Select id="qualidade-lote" value={form.lote_id || ''} onChange={(e) => setForm({ ...form, lote_id: e.target.value })} disabled={loading}><option value="">Selecione</option>{lotesValidos.map((lote) => <option key={lote.id} value={getUuidCandidate(lote) || ''}>{lote.numero_lote}</option>)}</Select></Field>
            <Field label="Ordem de produção" htmlFor="qualidade-op"><Select id="qualidade-op" value={form.op_id || ''} onChange={(e) => setForm({ ...form, op_id: e.target.value })} disabled={loading}><option value="">Selecione</option>{ordensValidas.map((op) => <option key={op.id} value={getUuidCandidate(op) || ''}>{op.numero}</option>)}</Select></Field>
            <Field label="Data da inspeção" htmlFor="qualidade-data"><Input id="qualidade-data" type="datetime-local" value={form.data_inspecao || ''} onChange={(e) => setForm({ ...form, data_inspecao: e.target.value })} disabled={loading} /></Field>
            <Field label="Responsável" htmlFor="qualidade-responsavel"><Input id="qualidade-responsavel" value={form.responsavel || ''} onChange={(e) => setForm({ ...form, responsavel: e.target.value })} disabled={loading} /></Field>
            <Field label="Ação corretiva" htmlFor="qualidade-acao" className="nx-sngpc-field--full"><Textarea id="qualidade-acao" value={form.acao_corretiva || ''} onChange={(e) => setForm({ ...form, acao_corretiva: e.target.value })} disabled={loading} rows={2} /></Field>
            <Field label="Observações" htmlFor="qualidade-observacoes" className="nx-sngpc-field--full"><Textarea id="qualidade-observacoes" value={form.observacoes || ''} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} disabled={loading} rows={3} /></Field>
          </div>
        </section>
      </Card>
      <FormStickyFooter cancelLabel="Cancelar" onCancel={() => navigate('/producao/qualidade')} saveLabel="Salvar" loading={salvando} saveIcon={<Save size={16} />} />
    </form>
  );
}
